import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { getDashboard, logWeight, getWeightLogs, updateWeightLog, deleteWeightLog, generateRecipe } from '../lib/api';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { OfflinePage } from '../components/OfflineComponents';
import { AdBanner } from '../components/AdBanner';
import { safeSetItem } from '../utils/storageUtils';
import { trackWeightEntry, trackRecipeGenerated, trackRecipeSaved } from '../lib/analytics';
import { API_URL } from '../config/api';
import { startGlobalVideoPreload } from '../lib/videoPreloader';
import RunningManIcon from '../components/RunningManIcon';
import { 
  Scale, TrendingDown, Flame, Trophy, Plus, ChevronRight, ChevronLeft,
  Calendar, Target, CheckCircle2, MapPin, CalendarDays, Pencil, Trash2, X,
  ChefHat, Sparkles, Loader2, Star, Dumbbell, Medal, Award,
  Crown, PenLine, BookOpen, Users, Heart, Zap, UtensilsCrossed, Footprints,
  CalendarRange, Activity, Clock, Share2
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip, Legend } from 'recharts';
import NotificationBell from '../components/NotificationBell';
import MessagesIcon from '../components/MessagesIcon';
import TransformationCard from '../components/TransformationCard';
import GlobalAlertBanner from '../components/GlobalAlertBanner';
import NotificationOptInSheet from '../components/NotificationOptInSheet';
import SupportReplyBanner from '../components/SupportReplyBanner';
import InteractionsBanner from '../components/InteractionsBanner';
import CoachCard from '../components/CoachCard';
import EditJourneyButton from '../components/EditJourneyButton';
import ShareWeightModal from '../components/ShareWeightModal';
import { usePremiumStatus, useDashboardButtons, useAdBanner, useTodayActivity } from '../lib/queries';
import HealthPlugin from '../plugins/HealthPlugin';
import { useQueryClient } from '@tanstack/react-query';

// Badge icon mapping
const BADGE_ICONS = {
  Scale, Flame, Calendar, Trophy, Star, TrendingDown, Dumbbell, Medal,
  Award, Target, Crown, PenLine, BookOpen, Users, Heart, Zap
};

// Get a consistent phrase index for the day (changes daily, not on every render)
const getMotivationalIndex = () => {
  const today = new Date().toDateString();
  const cached = sessionStorage.getItem('motivational_index_date');
  const cachedIndex = sessionStorage.getItem('motivational_index');
  
  if (cached === today && cachedIndex !== null) {
    return parseInt(cachedIndex);
  }
  
  const randomIndex = Math.floor(Math.random() * 9); // 9 phrases
  
  sessionStorage.setItem('motivational_index_date', today);
  sessionStorage.setItem('motivational_index', randomIndex.toString());
  
  return randomIndex;
};

// Get gender key for translations (m or f)
const getGenderKey = (sex) => {
  // sex can be 'M', 'F', 'male', 'female', 'other' or null
  if (!sex) return 'm';
  const s = sex.toLowerCase();
  if (s === 'f' || s === 'female') return 'f';
  return 'm';
};

// Get cached dashboard data synchronously
const getCachedDashboard = () => {
  try {
    const cached = localStorage.getItem('dashboard_cache');
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Cache valid for 5 minutes
      if (data && Date.now() - timestamp < 5 * 60 * 1000) {
        return data;
      }
    }
  } catch (e) {}
  return null;
};

export default function Dashboard() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useTranslation();
  const { formatWeight, formatNumber, convertWeight, weightUnit, convertDistance, distanceUnit } = useUnits();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  // Move/Esercizio section visibility (admin-controlled global toggle).
  // Defaults true; admins always see the card.
  const [moveSectionEnabled, setMoveSectionEnabled] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/public`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => { if (!cancelled && cfg) setMoveSectionEnabled(cfg.move_enabled !== false); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  
  // Initialize from cache IMMEDIATELY (synchronous)
  const cachedData = getCachedDashboard();
  const [data, setData] = useState(cachedData);
  const [loading, setLoading] = useState(!cachedData); // No loading if cache exists
  const [networkError, setNetworkError] = useState(false);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [chartPeriod, setChartPeriod] = useState('all'); // 7d, 14d, 30d, all
  
  // Chef AI state
  const [showChefModal, setShowChefModal] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState('pranzo');
  const [generatingRecipe, setGeneratingRecipe] = useState(false);
  const [recipeError, setRecipeError] = useState('');
  const [isPremium, setIsPremium] = useState(() => {
    try { const c = JSON.parse(sessionStorage.getItem('dash_cache') || '{}'); return c.isPremium || false; } catch { return false; }
  });
  const [trialUsed, setTrialUsed] = useState(() => {
    try { const c = JSON.parse(sessionStorage.getItem('dash_cache') || '{}'); return c.trialUsed ?? true; } catch { return true; }
  });
  // Badge "Passa a Premium": nascondibile dall'admin (Impostazioni)
  const [premiumBadgeEnabled, setPremiumBadgeEnabled] = useState(() => localStorage.getItem('premium_badge_enabled') !== 'false');
  useEffect(() => {
    fetch(`${API_URL}/api/settings/public`)
      .then((r) => r.json())
      .then((s) => {
        const v = s.premium_badge_enabled !== false;
        setPremiumBadgeEnabled(v);
        localStorage.setItem('premium_badge_enabled', String(v));
      })
      .catch(() => {});
  }, []);

  const [daysRemaining, setDaysRemaining] = useState(() => {
    try { const c = JSON.parse(sessionStorage.getItem('dash_cache') || '{}'); return c.daysRemaining ?? null; } catch { return null; }
  });
  const [isTrial, setIsTrial] = useState(() => {
    try { const c = JSON.parse(sessionStorage.getItem('dash_cache') || '{}'); return c.isTrial || false; } catch { return false; }
  });
  const [premiumLoaded, setPremiumLoaded] = useState(() => !!sessionStorage.getItem('dash_cache'));
  const [dashboardReady, setDashboardReady] = useState(() => {
    // If we have cached data, show immediately - no transition needed
    return !!getCachedDashboard() && !!sessionStorage.getItem('dash_cache');
  });

  // Dashboard Buttons configuration
  const [buttonConfig, setButtonConfig] = useState(() => {
    try { const c = JSON.parse(sessionStorage.getItem('dash_cache') || '{}'); return c.buttonConfig || null; } catch { return null; }
  });

  // Pre-cached ad banner for instant render
  const [cachedAdBanner, setCachedAdBanner] = useState(() => {
    try { const c = JSON.parse(sessionStorage.getItem('dash_cache') || '{}'); return c.adBanner || null; } catch { return null; }
  });

  // ═══════════════════════════════════════════════════════════════
  // React Query hooks — replace the three chained httpClient.get calls
  // inside loadDashboard. These are now deduplicated across components
  // (premium status is also used in Profilo, MealPlan, Premium pages).
  // ═══════════════════════════════════════════════════════════════
  const { data: premiumData } = usePremiumStatus();
  const { data: buttonsData } = useDashboardButtons();
  // Health activity (steps/km/kcal from Apple Health or Google Fit).
  // Hidden when no data and no streak — see render guard below.
  const { data: todayActivity } = useTodayActivity();
  const queryClient = useQueryClient();
  // Only fetch ad banner for non-premium users (avoid pointless requests)
  const { data: bannerData } = useAdBanner('home', { enabled: !(premiumData?.is_premium) });

  // Sync React Query data into local state for backward compatibility with
  // the existing render tree and sessionStorage caching layer.
  useEffect(() => {
    if (premiumData) {
      setIsPremium(premiumData.is_premium || false);
      setTrialUsed(premiumData.trial_used ?? true);
      setDaysRemaining(premiumData.days_remaining ?? null);
      setIsTrial(premiumData.is_trial ?? false);
      setPremiumLoaded(true);
    }
  }, [premiumData]);

  useEffect(() => {
    if (buttonsData) setButtonConfig(buttonsData);
  }, [buttonsData]);

  useEffect(() => {
    if (bannerData?.image_url) setCachedAdBanner(bannerData);
  }, [bannerData]);

  // ─── Auto-sync HealthKit / Google Fit on Dashboard mount ──────────
  // Silent sync every time the user lands on the Dashboard. Pulls the
  // last 7 days of steps + weight from the native health platform and
  // pushes to the backend. No UI feedback (banner removed by request).
  // iOS/Android only (no web).
  // Auto-sync HealthKit / Google Fit data into backend, then refresh
  // the dashboard's today activity query. Runs on:
  //  - first mount (initial open)
  //  - tab/window regaining visibility (user comes back from background)
  //  - window focus (user returns to PWA / desktop tab)
  // Web users (HealthPlugin.isNative() === false) are skipped silently.
  const syncHealthAndRefresh = useCallback(async () => {
    try {
      if (!HealthPlugin.isNative()) return;
      const avail = await HealthPlugin.isHealthDataAvailable();
      if (!avail?.available) return;
      await HealthPlugin.syncHealthDataToBackend(API_URL, 7);
      // Invalidate so React Query refetches /api/health/today and the
      // step counter on the dashboard updates immediately.
      queryClient.invalidateQueries({ queryKey: ['health', 'today'] });
    } catch (err) {
      console.warn('[Dashboard] health auto-sync failed:', err?.message || err);
    }
  }, [queryClient]);

  useEffect(() => {
    syncHealthAndRefresh();
  }, [syncHealthAndRefresh]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') syncHealthAndRefresh();
    };
    const onFocus = () => syncHealthAndRefresh();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [syncHealthAndRefresh]);

  // Keep session cache in sync so next navigation is instant.
  useEffect(() => {
    try {
      if (premiumData || buttonsData || bannerData) {
        sessionStorage.setItem('dash_cache', JSON.stringify({
          isPremium: premiumData?.is_premium || false,
          trialUsed: premiumData?.trial_used ?? true,
          daysRemaining: premiumData?.days_remaining ?? null,
          isTrial: premiumData?.is_trial ?? false,
          buttonConfig: buttonsData || null,
          adBanner: bannerData || null,
        }));
      }
    } catch (e) {}
  }, [premiumData, buttonsData, bannerData]);

  const loadDashboard = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      setNetworkError(false);

      // Hard 15 s timeout so a hanging backend can never leave the user
      // stuck on an infinite loading spinner (seen on the deployed PWA).
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('dashboard_timeout')), 15000);
      });
      const res = await Promise.race([getDashboard(), timeoutPromise]);
      setData(res.data);
      
      // Save to cache with quota management
      safeSetItem('dashboard_cache', {
        data: res.data,
        timestamp: Date.now()
      });
    } catch (err) {
      console.error('Dashboard error:', err);
      // If we have no cached data at all, surface a network error instead
      // of rendering nothing — otherwise the user sees a blank screen.
      if (!getCachedDashboard()) {
        setNetworkError(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load fresh data in background (cache is already showing)
    loadDashboard(false);

    // Feed prefetching is now handled globally by <PrefetchWarmer /> via
    // React Query's prefetchInfiniteQuery (see components/PrefetchWarmer.js).
    // No local prefetch needed here.

    // Start preloading video blobs for instant fullscreen playback
    startGlobalVideoPreload(API_URL);

    // Preload Feed chunk (route-level code-split)
    import('./Feed').catch(() => {});
  }, [loadDashboard]);

  // Scroll lock for Chef modal - prevent background scrolling
  useEffect(() => {
    if (showChefModal) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [showChefModal]);

  // Show dashboard when data is available (no rAF delay needed)
  useEffect(() => {
    if (data && !dashboardReady) {
      setDashboardReady(true);
    }
  }, [data, dashboardReady]);

  // Chef AI functions
  const handleGenerateRecipe = async () => {
    setGeneratingRecipe(true);
    setRecipeError('');

    try {
      const res = await generateRecipe(selectedMealType, language);
      // Track recipe generation in analytics
      trackRecipeGenerated(selectedMealType);
      // Navigate to full recipe page
      setShowChefModal(false);
      setGeneratingRecipe(false);
      navigate('/app/ricetta', { 
        state: { 
          recipe: res.data.recipe, 
          imageUrl: res.data.image_url 
        } 
      });
    } catch (err) {
      if (err.response?.status === 429) {
        setRecipeError(t('chef.alreadyGenerated'));
      } else {
        setRecipeError('Errore nella generazione. Riprova.');
      }
      setGeneratingRecipe(false);
    }
  };

  // Helper to get button style from config
  const getButtonStyle = (buttonId) => {
    const defaultStyles = {
      weight: { gradientFrom: '#16A34A', gradientTo: '#14B8A6', backgroundImage: '', opacity: 1, imageFadeStart: 0, imageFadeEnd: 100, height: 72, iconSize: 24, fontSize: 16 },
      walk: { gradientFrom: '#1d3557', gradientTo: '#1d3557', backgroundImage: '', opacity: 1, imageFadeStart: 0, imageFadeEnd: 100, height: 72, iconSize: 24, fontSize: 16 },
      chef: { gradientFrom: '#EC4899', gradientTo: '#F59E0B', backgroundImage: '', opacity: 1, imageFadeStart: 0, imageFadeEnd: 100, height: 72, iconSize: 24, fontSize: 16 },
      recipes: { gradientFrom: '#8B5CF6', gradientTo: '#A855F7', backgroundImage: '', opacity: 1, imageFadeStart: 0, imageFadeEnd: 100, height: 72, iconSize: 24, fontSize: 16 },
      workouts: { gradientFrom: '#0EA5E9', gradientTo: '#06B6D4', backgroundImage: '', opacity: 1, imageFadeStart: 0, imageFadeEnd: 100, height: 72, iconSize: 24, fontSize: 16 },
      mealplan: { gradientFrom: '#10B981', gradientTo: '#14B8A6', backgroundImage: '', opacity: 1, imageFadeStart: 0, imageFadeEnd: 100, height: 72, iconSize: 24, fontSize: 16 },
    };
    
    if (buttonConfig) {
      const btn = buttonConfig.find(b => b.id === buttonId);
      if (btn) {
        return {
          gradientFrom: btn.gradientFrom || defaultStyles[buttonId].gradientFrom,
          gradientTo: btn.gradientTo || defaultStyles[buttonId].gradientTo,
          backgroundImage: btn.backgroundImage || '',
          opacity: btn.opacity ?? 1,
          imageFadeStart: btn.imageFadeStart ?? 0,
          imageFadeEnd: btn.imageFadeEnd ?? 100,
          height: btn.height ?? 72,
          iconSize: btn.iconSize ?? 24,
          fontSize: btn.fontSize ?? 16,
        };
      }
    }
    return defaultStyles[buttonId];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  // Fetch failed AND we have no cached data to fall back to — offer a
  // retry instead of an empty screen.
  if (networkError && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-6 text-center">
        <div className={`text-base font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('dashboard.networkError', 'Errore di rete. Controlla la connessione.')}
        </div>
        <button
          onClick={() => { setLoading(true); loadDashboard(true); }}
          className="px-5 py-2.5 rounded-xl bg-[#E8334A] text-white font-semibold"
          data-testid="dashboard-retry-btn"
        >
          {t('common.retry', 'Riprova')}
        </button>
      </div>
    );
  }

  // If no data yet, show loading or redirect to create plan
  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!data?.has_plan) {
    return <NoPlan onCreatePlan={() => navigate('/app/nuovo-piano')} isDark={isDark} />;
  }

  const { plan, today, current_weight, weight_lost, progress_percent, chart_data, badges } = data;
  // Aggregated stats coming from `/api/dashboard.stats` — used by the
  // second row of cards and the secondary info strip below it.
  const stats = data?.stats || {};
  const totalKm = stats.total_km ?? 0;
  const totalCalories = stats.total_calories ?? 0;
  const workoutCount = stats.workout_count ?? 0;
  const weightLogsCount = stats.weight_logs_count ?? 0;
  const daysLeft = stats.days_remaining ?? 0;
  const weightRemaining = data?.weight_remaining ?? 0;

  // Filter chart data based on selected period
  const filterChartData = () => {
    if (chartPeriod === 'all') return chart_data;
    
    const daysMap = { '7d': 7, '14d': 14, '30d': 30 };
    const days = daysMap[chartPeriod] || chart_data.length;
    
    // Get the last N days that have actual data or show from today backwards
    const todayIndex = chart_data.findIndex(d => d.date === new Date().toISOString().split('T')[0]);
    const startIndex = Math.max(0, todayIndex - days + 1);
    
    return chart_data.slice(startIndex, todayIndex + 1);
  };

  const chartDataFiltered = filterChartData();
  
  // Prepare chart data — show both expected and actual curves.
  // Both values come from backend in KG; convert to the user's
  // preferred weight unit (kg or lbs) so the Y-axis and the
  // ReferenceLine match the rest of the UI.
  const chartDataFormatted = chartDataFiltered.map(d => ({
    ...d,
    expected: d.expected != null ? convertWeight(d.expected) : null,
    actual: d.actual != null ? convertWeight(d.actual) : null,
  }));

  return (
    <div 
      className={`page-enter min-h-screen ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} 
      data-testid="dashboard"
      style={{ opacity: dashboardReady ? 1 : 0 }}
    >
      
      {/* Fixed Header with notification bell - with safe area */}
      <div className={`fixed-header px-4 pt-4 pb-4 border-b ${isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-100'} shadow-sm`}>
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div>
            <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t(`dashboard.motivational.${getGenderKey(data.user?.sex)}.${getMotivationalIndex()}`)}</p>
            <h1 className={`font-display text-xl flex items-center gap-1.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {isPremium && <Crown className="w-5 h-5 text-amber-500" />}
              {data.user?.name?.split(' ')[0] || 'Utente'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <MessagesIcon />
            <NotificationBell />
          </div>
        </div>
      </div>

      {/* Scrollable Content - with padding for fixed header + safe area */}
      <div className="content-below-header pb-4">
      
      {/* Global Alert Banner */}
      <GlobalAlertBanner />
      <NotificationOptInSheet />

      {/* Support reply banner — surfaces unread admin messages */}
      <SupportReplyBanner />

      {/* Peer interactions banner — surfaces likes/comments/follows
          on the user's content. Opens the bell on tap. */}
      <InteractionsBanner />

      {/* Slimmy Coach — dynamic card */}
      <div className="px-4" style={{ marginTop: '16px' }}>
        <CoachCard />
      </div>
      
      {/* Smart Premium Banner */}
      {/* Show only: 1) Non-premium users OR 2) Trial users with ≤7 days remaining */}
      {premiumBadgeEnabled && (!isPremium || (isTrial && daysRemaining !== null && daysRemaining <= 7)) && (
        <div className="px-4 mb-3" style={{ marginTop: '20px' }}>
          <button
            onClick={() => navigate('/app/premium')}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl"
            style={{
              background: isTrial && daysRemaining !== null && daysRemaining <= 7
                ? 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)' // Urgency orange/red
                : 'linear-gradient(135deg, #E8334A 0%, #F97316 100%)',
            }}
          >
            <Crown className="w-5 h-5 text-white/90 flex-shrink-0" />
            <span className="flex-1 text-left text-white font-semibold text-sm">
              {isTrial && daysRemaining !== null && daysRemaining <= 7
                ? t('dashboard.premium.trialExpiring', { days: daysRemaining })
                : t('dashboard.premium.goTo')
              }
            </span>
            {!isPremium && !trialUsed && (
              <span className="text-white/80 text-xs">{t('dashboard.premium.freeTrial')}</span>
            )}
            {isTrial && daysRemaining !== null && daysRemaining <= 7 && (
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-white text-xs font-bold">
                {daysRemaining === 0 ? t('dashboard.premium.today') : `${daysRemaining}d`}
              </span>
            )}
            <ChevronRight className="w-4 h-4 text-white/70 flex-shrink-0" />
          </button>
        </div>
      )}
      
      {/* Health Activity Banner — visible when user has steps today OR an active streak.
          Steps come from Apple Health / Google Fit (synced from native HealthKit). */}
      {todayActivity && (todayActivity.has_data || todayActivity.streak_days > 0) && (
        <div className="px-4 mb-3" data-testid="dashboard-activity-banner">
          <div className={`relative overflow-hidden rounded-2xl p-3.5 border ${
            isDark
              ? 'bg-gradient-to-br from-emerald-500/15 to-teal-500/10 border-emerald-400/25'
              : 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                  <Footprints className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className={`text-[11px] uppercase tracking-wide ${isDark ? 'text-white/55' : 'text-gray-500'}`}>
                    {t('dashboard.activity.today', 'Attività di oggi')}
                  </p>
                  <p className={`font-display text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {formatNumber(todayActivity.steps || 0)}{' '}
                    <span className="text-xs font-normal opacity-70">
                      {t('dashboard.activity.steps', 'passi')}
                    </span>
                  </p>
                </div>
              </div>
              {todayActivity.streak_days > 0 && (
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                    isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'
                  }`}
                  data-testid="activity-streak-chip"
                >
                  <span role="img" aria-label="fire">🔥</span>
                  {todayActivity.streak_days}
                </div>
              )}
            </div>
            {/* Progress bar towards step goal */}
            <div className={`h-1.5 rounded-full overflow-hidden mb-2 ${isDark ? 'bg-white/10' : 'bg-emerald-200/50'}`}>
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                style={{ width: `${todayActivity.progress_pct || 0}%` }}
              />
            </div>
            {/* Distance + calories */}
            <div className="flex items-center justify-between text-[11px]">
              <span className={isDark ? 'text-white/55' : 'text-gray-500'} data-testid="activity-km-kcal">
                {convertDistance(todayActivity.km || 0).toFixed(2)} {distanceUnit} · {todayActivity.active_calories || 0} kcal
              </span>
              <span className={isDark ? 'text-emerald-300' : 'text-emerald-700'}>
                {todayActivity.progress_pct >= 100
                  ? t('dashboard.activity.goalReached', 'Obiettivo raggiunto! 🎉')
                  : `${todayActivity.progress_pct || 0}% ${t('dashboard.activity.of', 'di')} ${formatNumber(todayActivity.step_goal || 8000)}`}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Today Status Card */}
      <div className="px-4 mb-3" style={{ marginTop: '20px' }}>
        <div 
          className="glass-card p-4 cursor-pointer active:scale-[0.98] transition-transform"
          onClick={() => setShowWeightModal(true)}
          data-testid="today-status-card"
        >
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className={`text-xs mb-0.5 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('common.today')}, {formatDate(today.date)}
              </p>
              <div className="flex items-baseline gap-2">
                {today.actual ? (
                  <>
                    <span className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {formatNumber(convertWeight(today.actual), 1)} {weightUnit}
                    </span>
                    <StatusPill actual={today.actual} expected={today.expected} />
                  </>
                ) : (
                  <span className="font-display text-lg text-amber-500 animate-pulse flex items-center gap-2">
                    <Scale className="w-4 h-4" />
                    {t('dashboard.weighToday')}
                  </span>
                )}
              </div>
            </div>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
              today.logged 
                ? isDark ? 'bg-green-500/20' : 'bg-green-500/10'
                : 'bg-amber-500/20'
            }`}>
              {today.logged ? (
                <CheckCircle2 className="w-5 h-5 text-[#16A34A]" />
              ) : (
                <Plus className="w-5 h-5 text-amber-500 animate-bounce" />
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 text-xs">
            <Target className={`w-3.5 h-3.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            <span className={isDark ? 'text-white/60' : 'text-gray-500'}>{t('dashboard.goal')}:</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatNumber(convertWeight(plan.target_weight), 1)} {weightUnit}</span>
          </div>
        </div>
      </div>

      {/* Ad Banner - Home position */}
      <div className="px-4">
        <AdBanner position="home" className="mb-4" prefetchedBanner={cachedAdBanner} isPremiumKnown={!isPremium} />
      </div>

      {/* AI Body Transformation Card - More visible */}
      <div className="px-4 mb-4">
        <TransformationCard user={data.user} isDark={isDark} />
      </div>

      {/* Stats Row */}
      <div className="px-4 mb-4">
        <div className="grid grid-cols-3 gap-3">
          <StatCard 
            icon={<TrendingDown className="w-5 h-5 text-[#16A34A]" />}
            value={`-${formatNumber(convertWeight(weight_lost), 1)}`}
            unit={weightUnit}
            label={t('dashboard.lost')}
            isDark={isDark}
          />
          <StatCard 
            icon={<Flame className="w-5 h-5 text-[#F59E0B]" />}
            value={data.user?.streak_days || 0}
            unit={language === 'it' ? 'gg' : t('common.days').slice(0, 1)}
            label={t('dashboard.streak')}
            isDark={isDark}
          />
          <StatCard 
            icon={<Trophy className="w-5 h-5 text-[#E8334A]" />}
            value={data.user?.points || 0}
            unit="pt"
            label={t('dashboard.points')}
            isDark={isDark}
          />
        </div>

        {/* Row 2 — movement / activity stats. Same StatCard component so
            the visuals stay perfectly aligned with the row above. */}
        <div className="grid grid-cols-3 gap-3 mt-3">
          <StatCard
            icon={<Footprints className="w-5 h-5 text-[#3B82F6]" />}
            value={formatNumber(convertDistance(totalKm), totalKm < 10 ? 2 : 1)}
            unit={distanceUnit}
            label={(t('dashboard.totalKm', 'Km totali') || '').replace('Km', distanceUnit === 'km' ? 'Km' : distanceUnit).replace('km', distanceUnit)}
            isDark={isDark}
          />
          <StatCard
            icon={<Activity className="w-5 h-5 text-[#8B5CF6]" />}
            value={workoutCount}
            unit={workoutCount === 1 ? '' : ''}
            label={t('dashboard.workouts', 'Allenamenti')}
            isDark={isDark}
          />
          <StatCard
            icon={<Zap className="w-5 h-5 text-[#F97316]" />}
            value={formatNumber(totalCalories, 0)}
            unit="kcal"
            label={t('dashboard.calories', 'Calorie')}
            isDark={isDark}
          />
        </div>

        {/* Mini info strip — secondary metrics with a flatter visual style */}
        <div
          className={`mt-3 grid grid-cols-3 gap-2 rounded-xl px-2 py-2 ${
            isDark ? 'bg-white/5' : 'bg-gray-100/70'
          }`}
          data-testid="dashboard-mini-strip"
        >
          <MiniStat
            icon={<Target className="w-3.5 h-3.5 text-[#16A34A]" />}
            label={weightUnit}
            value={`-${formatNumber(convertWeight(Math.max(0, weightRemaining)), 0)}`}
            isDark={isDark}
          />
          <MiniStat
            icon={<Scale className="w-3.5 h-3.5 text-[#3B82F6]" />}
            label={t('dashboard.weighIns', 'pesate')}
            value={weightLogsCount}
            isDark={isDark}
          />
          <MiniStat
            icon={<Clock className="w-3.5 h-3.5 text-[#F59E0B]" />}
            label={t('dashboard.daysLeft', 'giorni')}
            value={daysLeft}
            isDark={isDark}
          />
        </div>
      </div>

      {/* Progress Card — circular ring + side info, primary red theme */}
      <div className="px-4 mb-6">
        <div className="rounded-2xl p-4 shadow-lg" style={{ backgroundColor: '#E8334A' }}>
          {(() => {
            const pct = Math.max(0, Math.min(100, Math.round(progress_percent || 0)));
            // Determine goal type from plan delta.
            const startW = plan.start_weight || plan.initial_weight || 0;
            const targetW = plan.target_weight || 0;
            const delta = targetW - startW;
            const goalKey =
              delta < -0.05 ? 'weightLoss'
              : delta > 0.05 ? 'weightGain'
              : 'maintain';
            const goalLabel = t(`dashboard.progressCard.${goalKey}`,
              goalKey === 'weightLoss' ? 'Dimagrimento'
              : goalKey === 'weightGain' ? 'Aumento massa'
              : 'Mantenimento');
            // Weight loss UX: show absolute kg moved so far.
            const lostKg = Math.abs(weight_lost || 0);
            const lostSign = (weight_lost || 0) > 0 ? '-' : ((weight_lost || 0) < 0 ? '+' : '');
            const subLabel = goalKey === 'weightGain'
              ? t('dashboard.progressCard.gainedSoFar', 'Guadagnati finora')
              : t('dashboard.progressCard.lostSoFar', 'Persi finora');

            // SVG ring geometry (radius 38, stroke 8).
            const RADIUS = 38;
            const CIRC = 2 * Math.PI * RADIUS;
            const dashOffset = CIRC * (1 - pct / 100);

            return (
              <div className="flex items-center gap-4" data-testid="progress-card">
                <div className="relative w-24 h-24 flex-shrink-0">
                  <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
                    <circle cx="50" cy="50" r={RADIUS} fill="none" strokeWidth="8" stroke="rgba(255,255,255,0.25)" />
                    <circle
                      cx="50" cy="50" r={RADIUS} fill="none" stroke="#ffffff" strokeWidth="8"
                      strokeLinecap="round" strokeDasharray={CIRC} strokeDashoffset={dashOffset}
                      style={{ transition: 'stroke-dashoffset 600ms ease' }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-display text-2xl text-white">{pct}%</span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/70">{t('dashboard.goal')}</p>
                  <p className="font-display text-base mb-2 truncate text-white">{goalLabel}</p>
                  <p className="font-display text-xl text-white">
                    {lostSign}{formatNumber(convertWeight(lostKg), 1)} {weightUnit}
                  </p>
                  <p className="text-xs text-white/70">{subLabel}</p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Weight Chart with Expected Curve */}
      <div className="px-4 mb-6">
        <div className="glass-card p-4" style={{ outline: 'none' }} tabIndex={-1}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-display ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('weightEntry.trend')}
            </h3>
            <button
              onClick={() => setShowShareModal(true)}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                isDark
                  ? 'bg-white/10 text-white hover:bg-white/15'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="share-weight-btn"
              aria-label={t('weightShare.shareBtn', 'Condividi')}
            >
              <Share2 className="w-3.5 h-3.5" />
              <span>{t('weightShare.shareBtn', 'Condividi')}</span>
            </button>
          </div>
          
          {/* Period Selector - All visible on screen */}
          <div className="flex gap-2 mb-4">
            {[
              { value: '7d', label: `7${t('common.days').charAt(0)}` },
              { value: '14d', label: `14${t('common.days').charAt(0)}` },
              { value: '30d', label: `30${t('common.days').charAt(0)}` },
              { value: 'all', label: t('common.all') }
            ].map(period => (
              <button
                key={period.value}
                onClick={() => setChartPeriod(period.value)}
                className={`flex-1 px-3 py-2 rounded-full text-sm font-medium transition-all outline-none focus:outline-none ${
                  chartPeriod === period.value
                    ? 'bg-[#1d3557] text-white'
                    : isDark 
                      ? 'bg-white/5 text-white/60 hover:bg-white/10' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
          
          {/* Chart container - completely disable focus and tap highlight */}
          <div 
            className="h-56 select-none focus:outline-none" 
            style={{ 
              WebkitTapHighlightColor: 'transparent', 
              outline: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none'
            }}
            tabIndex={-1}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartDataFormatted} margin={{ top: 5, right: 5, bottom: 5, left: -20 }} style={{ outline: 'none' }}>
                <XAxis 
                  dataKey="label" 
                  stroke={isDark ? '#64748B' : '#94A3B8'} 
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  domain={['auto', 'auto']}
                  stroke={isDark ? '#64748B' : '#94A3B8'}
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip 
                  cursor={false}
                  contentStyle={{ 
                    background: isDark ? 'rgba(8, 13, 26, 0.95)' : 'white', 
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                    borderRadius: '12px',
                    color: isDark ? 'white' : '#0F172A'
                  }}
                  formatter={(value, name) => [
                    value ? `${formatNumber(convertWeight(value), 1)} ${weightUnit}` : 'N/A',
                    name === 'expected' ? t('dashboard.weightCard.expected') : t('dashboard.weightCard.actual')
                  ]}
                />
                <ReferenceLine 
                  y={convertWeight(plan.target_weight)} 
                  stroke="#16A34A" 
                  strokeDasharray="3 3"
                  strokeOpacity={0.5}
                  label={{ value: t('dashboard.goal'), fill: '#16A34A', fontSize: 10 }}
                />
                {/* Expected (predicted) curve - dashed line */}
                <Line 
                  type="monotone" 
                  dataKey="expected" 
                  stroke="#1B3A6B" 
                  strokeWidth={2}
                  strokeDasharray="8 4"
                  dot={false}
                  name="expected"
                />
                {/* Actual weight - solid line */}
                <Line 
                  type="monotone" 
                  dataKey="actual" 
                  stroke="#16A34A" 
                  strokeWidth={3}
                  dot={{ fill: '#16A34A', strokeWidth: 0, r: 4 }}
                  activeDot={{ fill: '#4ADE80', strokeWidth: 0, r: 6 }}
                  connectNulls
                  name="actual"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-6 mt-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-6 h-0.5 bg-[#16A34A] rounded"></div>
              <span className={isDark ? 'text-white/60' : 'text-gray-500'}>{t('dashboard.weightCard.actualCurve')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-0.5 bg-[#1B3A6B] rounded" style={{ borderStyle: 'dashed' }}></div>
              <span className={isDark ? 'text-white/60' : 'text-gray-500'}>{t('dashboard.weightCard.expectedCurve')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Move Card — daily micro-workouts (NEW). Same dimensions as the
          other quick-access cards: 72px height, 48px icon, chevron.
          Hidden for non-admin users when the admin toggle is OFF. */}
      {(moveSectionEnabled || user?.is_admin) && (
      <div className="px-4 mb-6">
        <button
          onClick={() => navigate('/app/move')}
          className="w-full flex items-center gap-4 active:scale-[0.98] transition-transform rounded-2xl relative overflow-hidden"
          style={{
            height: '72px',
            padding: '0 16px',
            background: 'linear-gradient(135deg, #E8334A 0%, #F59E0B 100%)',
          }}
          data-testid="open-move-btn"
        >
          <span className="absolute top-1.5 right-2.5 px-2 py-0.5 rounded-full bg-white text-[#E8334A] text-[10px] font-bold tracking-wide z-10">
            {t('move.newBadge', 'NEW')}
          </span>
          <div className="rounded-xl flex items-center justify-center bg-white/20" style={{ width: '48px', height: '48px' }}>
            <Dumbbell className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <h3 className="font-display text-white truncate" style={{ fontSize: '16px' }}>
              {t('move.cardTitle', 'Move')}
            </h3>
            <p className="text-white/85 font-medium truncate" style={{ fontSize: '14px' }}>
              {t('move.cardDetail', 'Allenamenti guidati da 5 a 12 minuti')}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-white/70" />
        </button>
      </div>
      )}

      {/* Walking Card */}
      <div className="px-4 mb-6">
        {(() => {
          const style = getButtonStyle('walk');
          return (
            <button 
              onClick={() => navigate('/app/camminata')}
              className="w-full flex items-center gap-4 active:scale-[0.98] transition-transform rounded-2xl relative overflow-hidden"
              style={{ 
                height: `${style.height}px`,
                padding: '0 16px',
                background: `linear-gradient(to right, ${style.gradientFrom}, ${style.gradientTo})`
              }}
              data-testid="start-walk-btn"
            >
              {style.backgroundImage && (
                <div 
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${style.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    maskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    WebkitMaskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    opacity: style.opacity
                  }}
                />
              )}
              <div 
                className="rounded-xl flex items-center justify-center bg-white/20 relative z-10"
                style={{ width: `${style.iconSize + 24}px`, height: `${style.iconSize + 24}px` }}
              >
                <RunningManIcon style={{ width: `${style.iconSize}px`, height: `${style.iconSize}px` }} className="text-white" />
              </div>
              <div className="flex-1 text-left relative z-10">
                <h3 className="font-display text-white" style={{ fontSize: `${style.fontSize}px` }}>
                  {t('dashboard.startWalk')}
                </h3>
                <p className="text-white/80 font-medium" style={{ fontSize: `${style.fontSize - 2}px` }}>
                  {t('dashboard.recommendedWalk')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/60 relative z-10" />
            </button>
          );
        })()}
      </div>

      {/* Leaderboard Card — quick access to the national leaderboard.
          Matches the dimensions of the Walking / Workouts cards above:
          72px height, 48px icon container, 24px icon, 16px title font. */}
      <div className="px-4 mb-6">
        <button
          onClick={() => navigate('/app/classifica')}
          className="w-full flex items-center gap-4 active:scale-[0.98] transition-transform rounded-2xl relative overflow-hidden"
          style={{
            height: '72px',
            padding: '0 16px',
            background: 'linear-gradient(135deg, #F59E0B 0%, #E8334A 100%)',
          }}
          data-testid="open-leaderboard-btn"
        >
          <div className="rounded-xl flex items-center justify-center bg-white/20" style={{ width: '48px', height: '48px' }}>
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <h3 className="font-display text-white truncate" style={{ fontSize: '16px' }}>
              {t('dashboard.openLeaderboard', 'Classifica Nazionale')}
            </h3>
            <p className="text-white/85 font-medium truncate" style={{ fontSize: '14px' }}>
              {t('dashboard.openLeaderboardSub', 'Scopri la tua posizione')}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-white/70" />
        </button>
      </div>

      {/* My Workouts Button */}
      <div className="px-4 mb-6">
        {(() => {
          const style = getButtonStyle('workouts');
          return (
            <button 
              onClick={() => navigate('/app/miei-allenamenti')}
              className="w-full flex items-center gap-4 active:scale-[0.98] transition-transform rounded-2xl relative overflow-hidden"
              style={{ 
                height: `${style.height}px`,
                padding: '0 16px',
                background: `linear-gradient(to right, ${style.gradientFrom}, ${style.gradientTo})`
              }}
              data-testid="my-workouts-btn"
            >
              {style.backgroundImage && (
                <div 
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${style.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    maskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    WebkitMaskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    opacity: style.opacity
                  }}
                />
              )}
              <div 
                className="rounded-xl flex items-center justify-center bg-white/20 relative z-10"
                style={{ width: `${style.iconSize + 24}px`, height: `${style.iconSize + 24}px` }}
              >
                <Footprints style={{ width: `${style.iconSize}px`, height: `${style.iconSize}px` }} className="text-white" />
              </div>
              <div className="flex-1 text-left relative z-10">
                <h3 className="font-display text-white" style={{ fontSize: `${style.fontSize}px` }}>{t('dashboard.myWorkouts')}</h3>
                <p className="text-white/80" style={{ fontSize: `${style.fontSize - 2}px` }}>{t('dashboard.myWorkoutsSub')}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/60 relative z-10" />
            </button>
          );
        })()}
      </div>

      {/* Insert Weight Card */}
      <div className="px-4 mb-6">
        {(() => {
          const style = getButtonStyle('weight');
          return (
            <button 
              onClick={() => navigate('/app/inserisci-peso')}
              className="w-full flex items-center gap-4 active:scale-[0.98] transition-transform rounded-2xl relative overflow-hidden"
              style={{ 
                height: `${style.height}px`,
                padding: '0 16px',
                background: `linear-gradient(to right, ${style.gradientFrom}, ${style.gradientTo})`
              }}
              data-testid="insert-weight-btn"
            >
              {style.backgroundImage && (
                <div 
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${style.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    maskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    WebkitMaskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    opacity: style.opacity
                  }}
                />
              )}
              <div 
                className="rounded-xl flex items-center justify-center bg-white/20 relative z-10"
                style={{ width: `${style.iconSize + 24}px`, height: `${style.iconSize + 24}px` }}
              >
                <Scale style={{ width: `${style.iconSize}px`, height: `${style.iconSize}px` }} className="text-white" />
              </div>
              <div className="flex-1 text-left relative z-10">
                <h3 className="font-display text-white" style={{ fontSize: `${style.fontSize}px` }}>{t('dashboard.insertWeight')}</h3>
                <p className="text-white/80" style={{ fontSize: `${style.fontSize - 2}px` }}>{t('dashboard.insertWeightSub')}</p>
              </div>
              <CalendarDays className="w-5 h-5 text-white/60 relative z-10" />
            </button>
          );
        })()}
      </div>

      {/* Chef AI Card */}
      <div className="px-4 mb-4">
        {(() => {
          const style = getButtonStyle('chef');
          return (
            <button 
              onClick={() => setShowChefModal(true)}
              className="w-full flex items-center gap-4 active:scale-[0.98] transition-transform rounded-2xl relative overflow-hidden"
              style={{ 
                height: `${style.height}px`,
                padding: '0 16px',
                background: `linear-gradient(to right, ${style.gradientFrom}, ${style.gradientTo})`
              }}
              data-testid="chef-ai-btn"
            >
              {style.backgroundImage && (
                <div 
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${style.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    maskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    WebkitMaskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    opacity: style.opacity
                  }}
                />
              )}
              <div 
                className="rounded-xl flex items-center justify-center bg-white/20 relative z-10"
                style={{ width: `${style.iconSize + 24}px`, height: `${style.iconSize + 24}px` }}
              >
                <ChefHat style={{ width: `${style.iconSize}px`, height: `${style.iconSize}px` }} className="text-white" />
              </div>
              <div className="flex-1 text-left relative z-10">
                <h3 className="font-display text-white" style={{ fontSize: `${style.fontSize}px` }}>{t('chef.title')}</h3>
                <p className="text-white/80" style={{ fontSize: `${style.fontSize - 2}px` }}>{t('dashboard.chefAiSub')}</p>
              </div>
              <Sparkles className="w-5 h-5 text-white/60 relative z-10" />
            </button>
          );
        })()}
      </div>

      {/* AI Meal Plan Button (Premium) */}
      <div className="px-4 mb-4">
        {(() => {
          const style = getButtonStyle('mealplan');
          return (
            <button 
              onClick={() => navigate('/app/piano-alimentare')}
              className="w-full flex items-center gap-4 active:scale-[0.98] transition-transform rounded-2xl relative overflow-hidden"
              style={{ 
                height: `${style.height}px`,
                padding: '0 16px',
                background: `linear-gradient(to right, ${style.gradientFrom}, ${style.gradientTo})`
              }}
              data-testid="meal-plan-btn"
            >
              {style.backgroundImage && (
                <div 
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${style.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    maskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    WebkitMaskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    opacity: style.opacity
                  }}
                />
              )}
              <div 
                className="rounded-xl flex items-center justify-center bg-white/20 relative z-10"
                style={{ width: `${style.iconSize + 24}px`, height: `${style.iconSize + 24}px` }}
              >
                <CalendarRange style={{ width: `${style.iconSize}px`, height: `${style.iconSize}px` }} className="text-white" />
              </div>
              <div className="flex-1 text-left relative z-10">
                <h3 className="font-display text-white" style={{ fontSize: `${style.fontSize}px` }}>{t('dashboard.mealPlan')}</h3>
                <p className="text-white/80" style={{ fontSize: `${style.fontSize - 2}px` }}>{t('dashboard.mealPlanSub')}</p>
              </div>
              {isPremium ? (
                <Crown className="w-5 h-5 text-amber-400 relative z-10" />
              ) : (
                <Crown className="w-5 h-5 text-white/60 relative z-10" />
              )}
            </button>
          );
        })()}
      </div>

      {/* My Recipes Button */}
      <div className="px-4 mb-4">
        {(() => {
          const style = getButtonStyle('recipes');
          return (
            <button 
              onClick={() => navigate('/app/mie-ricette')}
              className="w-full flex items-center gap-4 active:scale-[0.98] transition-transform rounded-2xl relative overflow-hidden"
              style={{ 
                height: `${style.height}px`,
                padding: '0 16px',
                background: `linear-gradient(to right, ${style.gradientFrom}, ${style.gradientTo})`
              }}
              data-testid="my-recipes-btn"
            >
              {style.backgroundImage && (
                <div 
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${style.backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    maskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    WebkitMaskImage: `linear-gradient(to right, transparent 0%, transparent ${style.imageFadeStart}%, black ${style.imageFadeStart + 10}%, black ${style.imageFadeEnd - 10}%, transparent ${style.imageFadeEnd}%, transparent 100%)`,
                    opacity: style.opacity
                  }}
                />
              )}
              <div 
                className="rounded-xl flex items-center justify-center bg-white/20 relative z-10"
                style={{ width: `${style.iconSize + 24}px`, height: `${style.iconSize + 24}px` }}
              >
                <UtensilsCrossed style={{ width: `${style.iconSize}px`, height: `${style.iconSize}px` }} className="text-white" />
              </div>
              <div className="flex-1 text-left relative z-10">
                <h3 className="font-display text-white" style={{ fontSize: `${style.fontSize}px` }}>{t('dashboard.myRecipes')}</h3>
                <p className="text-white/80" style={{ fontSize: `${style.fontSize - 2}px` }}>{t('dashboard.myRecipesSub')}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-white/60 relative z-10" />
            </button>
          );
        })()}
      </div>

      {/* Latest Weight Entries - Last 7 */}
      {chart_data && chart_data.length > 0 && (
        <div className="px-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className={`font-display ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('dashboard.latestEntries')}</h3>
            <button 
              onClick={() => setShowHistoryModal(true)}
              className={`flex items-center gap-1 text-sm px-3 py-1 rounded-full ${
                isDark ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Pencil className="w-3 h-3" />
              {t('weightHistory.edit')}
            </button>
          </div>
          <div className="glass-card overflow-hidden">
            {/* Table Header */}
            <div className={`grid grid-cols-5 gap-2 px-4 py-3 text-xs font-medium uppercase tracking-wider ${
              isDark ? 'text-white/40 border-b border-white/5' : 'text-gray-400 border-b border-gray-100'
            }`}>
              <span>{t('dashboard.table.date')}</span>
              <span>{t('dashboard.table.weight')}</span>
              <span>{t('dashboard.table.line')}</span>
              <span>{t('dashboard.table.diff')}</span>
              <span className="text-right">{t('dashboard.table.status')}</span>
            </div>
            
            {/* Table Rows - Last 7 entries */}
            <div className="divide-y divide-white/5">
              {chart_data
                .filter(d => d.actual != null)
                .slice(-7)
                .reverse()
                .map((entry, idx) => {
                  const actualConverted = convertWeight(entry.actual);
                  const expectedConverted = entry.expected ? convertWeight(entry.expected) : null;
                  const diff = expectedConverted ? (actualConverted - expectedConverted).toFixed(2) : 0;
                  const diffNum = parseFloat(diff);
                  const status = Math.abs(diffNum) < 0.1 ? 'on' : diffNum < 0 ? 'under' : 'over';
                  
                  return (
                    <div 
                      key={`weight-${entry.date}-${idx}`}
                      className={`grid grid-cols-5 gap-2 px-4 py-3 items-center ${
                        isDark ? '' : ''
                      }`}
                    >
                      <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        {new Date(entry.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                      </span>
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatNumber(actualConverted, 1)} {weightUnit}
                      </span>
                      <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        {expectedConverted ? `${formatNumber(expectedConverted, 1)} ${weightUnit}` : '-'}
                      </span>
                      <span className={`text-sm font-medium ${
                        status === 'under' ? 'text-green-500' : status === 'over' ? 'text-red-500' : 'text-blue-500'
                      }`}>
                        {diffNum > 0 ? '+' : ''}{formatNumber(diffNum, 2)} {weightUnit}
                      </span>
                      <span className="text-right">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                          status === 'under' 
                            ? 'bg-green-500/10 text-green-500' 
                            : status === 'over' 
                              ? 'bg-red-500/10 text-red-500' 
                              : 'bg-blue-500/10 text-blue-500'
                        }`}>
                          {status === 'under' ? t('dashboard.status.under') : status === 'over' ? t('dashboard.status.over') : t('dashboard.status.ok')}
                        </span>
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Badges */}
      {badges && badges.length > 0 && (
        <div className="px-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className={`font-display ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('dashboard.recentBadges')}</h3>
            <button 
              onClick={() => navigate('/app/profilo')}
              className="text-sm text-[#E8334A] flex items-center gap-1"
            >
              {t('dashboard.viewAll')} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {badges.slice(0, 6).map((badge, i) => {
              const IconComponent = BADGE_ICONS[badge.icon] || Award;
              const badgeColor = badge.color || '#F59E0B';
              return (
                <div 
                  key={badge.code || `badge-${i}`}
                  className="glass-card-sm p-3 text-center"
                >
                  <div 
                    className="w-12 h-12 mx-auto mb-2 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${badgeColor}20` }}
                  >
                    <IconComponent className="w-6 h-6" style={{ color: badgeColor }} />
                  </div>
                  <p className={`text-xs font-medium leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t(`badgeNames.${badge.code}`) || badge.name}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Plan Info */}
      <div className="px-4 mb-6">
        <div className="glass-card-sm p-4">
          <div className="flex items-center gap-3">
            <Calendar className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('dashboard.myJourney')}</p>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {formatDate(plan.start_date)} → {formatDate(plan.end_date)}
              </p>
            </div>
            <EditJourneyButton plan={plan} onUpdated={loadDashboard} />
          </div>
        </div>
      </div>
      
      </div>{/* End of pt-24 pb-4 scrollable content wrapper */}

      {/* Weight Modal */}
      {showWeightModal && (
        <WeightModal 
          today={today}
          previousWeight={current_weight}
          startWeight={plan?.start_weight}
          isDark={isDark}
          onClose={() => setShowWeightModal(false)}
          onSave={async (weight, mood) => {
            await logWeight({ weight, mood });
            // Track weight entry in analytics
            trackWeightEntry(weight, 'kg');
            setShowWeightModal(false);
            loadDashboard();
          }}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <WeightHistoryModal 
          plan={plan}
          isDark={isDark}
          onClose={() => setShowHistoryModal(false)}
          onUpdate={loadDashboard}
        />
      )}

      {/* Share Weight Modal — turns the selected chart period into a
          shareable PNG (feed / challenges / WhatsApp). */}
      <ShareWeightModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        isDark={isDark}
        user={data.user}
        plan={plan}
        chartData={chartDataFormatted}
        chartPeriod={chartPeriod}
        weightLost={weight_lost}
        streakDays={data.user?.streak_days || 0}
        formatNumber={formatNumber}
        convertWeight={convertWeight}
        weightUnit={weightUnit}
      />

      {/* Chef AI Modal */}
      {showChefModal && createPortal(
        <div 
          className="fixed inset-x-0 top-0 flex items-end justify-center bg-black/60 backdrop-blur-sm"
          style={{ zIndex: 40, bottom: '72px' }}
          onClick={() => !generatingRecipe && setShowChefModal(false)}
        >
          <div 
            className={`w-full max-w-md rounded-t-3xl p-6 animate-slide-up relative ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button - pink circle top right */}
            <button
              onClick={() => !generatingRecipe && setShowChefModal(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#EC4899] flex items-center justify-center shadow-lg"
              disabled={generatingRecipe}
              data-testid="chef-close-btn"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            
            <div className={`w-12 h-1 rounded-full mx-auto mb-6 sm:hidden ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
            
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#EC4899] to-[#F59E0B] flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('chef.title')}</h2>
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('chef.subtitle')}</p>
              </div>
            </div>

            {/* Meal Type Selector */}
            <div className="mb-6">
              <p className={`text-xs font-medium uppercase mb-3 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('chef.mealType')}
              </p>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { key: 'colazione', label: t('chef.breakfast') },
                  { key: 'spuntino', label: t('chef.snack') },
                  { key: 'pranzo', label: t('chef.lunch') },
                  { key: 'cena', label: t('chef.dinner') }
                ].map(meal => (
                  <button
                    key={meal.key}
                    onClick={() => setSelectedMealType(meal.key)}
                    className={`py-3 rounded-xl text-sm font-medium transition-all ${
                      selectedMealType === meal.key
                        ? 'bg-[#EC4899] text-white'
                        : isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {meal.label}
                  </button>
                ))}
              </div>
            </div>

            {recipeError && (
              <div className={`p-3 rounded-xl mb-4 ${isDark ? 'bg-red-500/10' : 'bg-red-50'}`}>
                <p className="text-[#E8334A] text-sm text-center">{recipeError}</p>
              </div>
            )}

            <button
              onClick={handleGenerateRecipe}
              disabled={generatingRecipe}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#EC4899] to-[#F59E0B] text-white font-semibold flex items-center justify-center gap-2"
            >
              {generatingRecipe ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('chef.generating')}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  {t('chef.generateRecipe')}
                </>
              )}
            </button>

            <p className={`text-xs mt-4 text-center ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              {isPremium ? (
                <span className="text-[#16A34A] flex items-center justify-center gap-1">
                  <Crown className="w-3 h-3" /> {t('chef.unlimitedPremium')}
                </span>
              ) : (
                t('chef.dailyLimit')
              )}
            </p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function NoPlan({ onCreatePlan, isDark }) {
  const { t } = useTranslation();
  return (
    <div className="page-enter flex flex-col items-center justify-center min-h-[70vh] px-6">
      {/* Global Alert Banner - also shown for new users */}
      <div className="w-full max-w-sm mb-6">
        <GlobalAlertBanner />
      </div>
      
      <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 ${
        isDark ? 'bg-[#1B3A6B]/50' : 'bg-[#1B3A6B]/10'
      }`}>
        <Scale className={`w-10 h-10 ${isDark ? 'text-white/60' : 'text-[#1B3A6B]'}`} />
      </div>
      <h2 className={`font-display text-2xl mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {t('dashboard.noPlan.title')}
      </h2>
      <p className={`text-center mb-8 max-w-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
        {t('dashboard.noPlan.subtitle')}
      </p>
      <button 
        onClick={onCreatePlan}
        className="btn-primary"
        data-testid="create-plan-btn"
      >
        {t('dashboard.noPlan.button')}
      </button>
    </div>
  );
}

function StatCard({ icon, value, unit, label, isDark }) {
  return (
    <div className="glass-card-sm p-3 text-center">
      <div className="flex justify-center mb-2">{icon}</div>
      <div className="flex items-baseline justify-center gap-1">
        <span className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</span>
        <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{unit}</span>
      </div>
      <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{label}</p>
    </div>
  );
}

function MiniStat({ icon, value, label, isDark }) {
  return (
    <div className="flex items-center justify-center gap-1.5 py-1">
      {icon}
      <span className={`font-semibold text-[13px] tabular-nums ${isDark ? 'text-white/90' : 'text-gray-800'}`}>
        {value}
      </span>
      <span className={`text-[11px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
  );
}

function StatusPill({ actual, expected }) {
  const { t } = useTranslation();
  const diff = actual - expected;
  let className = 'status-on';
  let text = t('dashboard.status.ok');
  
  if (diff < -0.2) {
    className = 'status-under';
    text = `${Math.abs(diff).toFixed(1)}kg ${t('dashboard.status.underShort')}`;
  } else if (diff > 0.2) {
    className = 'status-over';
    text = `${diff.toFixed(1)}kg ${t('dashboard.status.overShort')}`;
  }
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>
      {text}
    </span>
  );
}

function WeightModal({ today, previousWeight, startWeight, isDark, onClose, onSave }) {
  const { t } = useTranslation();
  // Default precedence:
  //   1. today's logged weight (so the user can edit it)
  //   2. previous logged weight (yesterday or earlier)
  //   3. plan's starting weight (first ever log scenario)
  //   4. hard fallback 80 (no plan and no logs at all — should be rare)
  const initialWeight = today.actual ?? previousWeight ?? startWeight ?? 80;
  const [weightKg, setWeightKg] = useState(Math.floor(initialWeight));
  const [weightEtti, setWeightEtti] = useState(Math.round((initialWeight - Math.floor(initialWeight)) * 10));
  const [mood, setMood] = useState(today.mood || '');
  const [saving, setSaving] = useState(false);

  const moods = ['😊', '😐', '😔', '💪', '😴'];
  const kgRange = Array.from({ length: 181 }, (_, i) => i + 20);
  const ettiRange = Array.from({ length: 10 }, (_, i) => i);

  // Block scroll on mount - more aggressive
  useEffect(() => {
    const originalStyle = document.body.style.cssText;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;
    
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.cssText = originalStyle;
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, []);

  const handleSave = async () => {
    const finalWeight = weightKg + (weightEtti / 10);
    setSaving(true);
    try {
      await onSave(finalWeight, mood);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return createPortal(
    <div 
      className="fixed inset-0 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      style={{ zIndex: 9999 }}
      onClick={handleClose}
    >
      <div 
        className={`w-full max-w-md rounded-t-3xl p-6 animate-slide-up ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
        style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}
        onClick={e => e.stopPropagation()}
      >
        <div className={`w-12 h-1 rounded-full mx-auto mb-6 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
        
        <h2 className={`font-display text-xl mb-6 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('weightEntry.insertToday')}
        </h2>

        {/* Weight Display */}
        <div className="text-center mb-3">
          <span className="font-display text-5xl font-bold text-[#1d3557]">
            {weightKg}.{weightEtti}
          </span>
          <span className={`text-xl ${isDark ? 'text-white/50' : 'text-gray-500'}`}> kg</span>
        </div>

        {/* Drum Labels */}
        <div className="flex justify-center gap-16 text-xs font-medium uppercase tracking-wider mb-2">
          <span className={isDark ? 'text-white/50' : 'text-gray-500'}>KG</span>
          <span className={isDark ? 'text-white/50' : 'text-gray-500'}>100G</span>
        </div>

        {/* Drum Picker */}
        <div className={`flex items-center justify-center rounded-xl overflow-hidden h-40 mb-6 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
          {/* KG Column */}
          <div className="relative h-full w-24 overflow-hidden">
            <div 
              className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
              style={{ scrollBehavior: 'smooth' }}
              onScroll={(e) => {
                const idx = Math.round(e.target.scrollTop / 40);
                if (kgRange[idx] !== undefined) setWeightKg(kgRange[idx]);
              }}
              ref={(el) => {
                if (el && !el.dataset.init) {
                  el.dataset.init = 'true';
                  const idx = kgRange.indexOf(weightKg);
                  if (idx !== -1) el.scrollTop = idx * 40;
                }
              }}
            >
              <div className="h-[60px]"></div>
              {kgRange.map(kg => (
                <div 
                  key={kg}
                  className={`h-10 flex items-center justify-center snap-center font-display transition-all ${
                    weightKg === kg 
                      ? 'text-[#1d3557] font-bold text-2xl'
                      : Math.abs(weightKg - kg) === 1
                        ? isDark ? 'text-white/40 text-lg' : 'text-gray-400 text-lg'
                        : isDark ? 'text-white/20' : 'text-gray-300'
                  }`}
                >
                  {kg}
                </div>
              ))}
              <div className="h-[60px]"></div>
            </div>
            <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-10 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
          </div>

          <span className={`font-display text-2xl ${isDark ? 'text-white/30' : 'text-gray-300'}`}>.</span>

          {/* Etti Column */}
          <div className="relative h-full w-16 overflow-hidden">
            <div 
              className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
              style={{ scrollBehavior: 'smooth' }}
              onScroll={(e) => {
                const idx = Math.round(e.target.scrollTop / 40);
                if (ettiRange[idx] !== undefined) setWeightEtti(ettiRange[idx]);
              }}
              ref={(el) => {
                if (el && !el.dataset.init) {
                  el.dataset.init = 'true';
                  el.scrollTop = weightEtti * 40;
                }
              }}
            >
              <div className="h-[60px]"></div>
              {ettiRange.map(e => (
                <div 
                  key={e}
                  className={`h-10 flex items-center justify-center snap-center font-display transition-all ${
                    weightEtti === e 
                      ? 'text-[#1d3557] font-bold text-2xl'
                      : Math.abs(weightEtti - e) === 1
                        ? isDark ? 'text-white/40 text-lg' : 'text-gray-400 text-lg'
                        : isDark ? 'text-white/20' : 'text-gray-300'
                  }`}
                >
                  {e}
                </div>
              ))}
              <div className="h-[60px]"></div>
            </div>
            <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-10 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
          </div>
        </div>

        {/* Mood */}
        <div className="mb-6">
          <label className={`text-sm mb-3 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('weightEntry.mood')}</label>
          <div className="flex justify-center gap-3">
            {moods.map(m => (
              <button
                key={m}
                onClick={() => setMood(m)}
                className={`w-12 h-12 rounded-full text-2xl flex items-center justify-center transition-all ${
                  mood === m 
                    ? 'bg-[#E8334A]/20 ring-2 ring-[#E8334A]' 
                    : isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={handleClose} className="btn-secondary flex-1">
            {t('common.cancel')}
          </button>
          <button 
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex-1"
            data-testid="save-weight-btn"
          >
            {saving ? t('weightHistory.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

// Weight History Modal with edit/delete functionality
function WeightHistoryModal({ plan, isDark, onClose, onUpdate }) {
  const { t, language } = useTranslation();
  const { formatNumber, convertWeight, weightUnit, convertWeightToKg } = useUnits();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLog, setEditingLog] = useState(null);
  const [editWeight, setEditWeight] = useState(80);
  const [editDecimal, setEditDecimal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Generate range based on unit (kg: 20-200, lbs: 44-440)
  const isLbs = weightUnit === 'lbs';
  const wholeRange = isLbs 
    ? Array.from({ length: 397 }, (_, i) => i + 44) // 44-440 lbs
    : Array.from({ length: 181 }, (_, i) => i + 20); // 20-200 kg
  const decimalRange = Array.from({ length: 10 }, (_, i) => i);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const res = await getWeightLogs();
      setLogs(res.data.reverse()); // Most recent first
    } catch (err) {
      console.error('Error loading logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (log) => {
    // Convert from kg to display unit
    const displayWeight = convertWeight(log.weight);
    const whole = Math.floor(displayWeight);
    const decimal = Math.round((displayWeight - whole) * 10);
    setEditWeight(whole);
    setEditDecimal(decimal);
    setEditingLog(log);
  };

  const saveEdit = async () => {
    if (!editingLog) return;
    setSaving(true);
    try {
      // Get weight in display unit and convert back to kg for storage
      const displayWeight = editWeight + (editDecimal / 10);
      const weightInKg = convertWeightToKg(displayWeight); // Converts lbs -> kg if needed
      await updateWeightLog(editingLog.log_id, { weight: weightInKg, mood: editingLog.mood });
      setEditingLog(null);
      loadLogs();
      onUpdate();
    } catch (err) {
      console.error('Error updating log:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (logId) => {
    try {
      await deleteWeightLog(logId);
      setConfirmDelete(null);
      loadLogs();
      onUpdate();
    } catch (err) {
      console.error('Error deleting log:', err);
    }
  };

  // Calculate expected weight for a date
  const getExpected = (dateStr) => {
    if (!plan || !plan.initial_weight || !plan.target_weight) return null;
    
    const start = new Date(plan.start_date);
    const end = new Date(plan.end_date);
    const current = new Date(dateStr);
    
    const totalDays = (end - start) / (1000 * 60 * 60 * 24);
    if (totalDays <= 0) return null;
    
    const elapsedDays = (current - start) / (1000 * 60 * 60 * 24);
    const progress = Math.min(1, Math.max(0, elapsedDays / totalDays));
    
    const totalLoss = plan.initial_weight - plan.target_weight;
    const expectedLoss = totalLoss * (1 - Math.exp(-3 * progress));
    const result = plan.initial_weight - expectedLoss;
    
    return isNaN(result) ? null : result.toFixed(1);
  };

  const handleClose = () => {
    onClose();
  };

  // Block body scroll
  useEffect(() => {
    const originalStyle = document.body.style.cssText;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;
    
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.cssText = originalStyle;
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, []);

  return createPortal(
    <div 
      className="fixed inset-0 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      style={{ zIndex: 9999 }}
      onClick={handleClose}
    >
      <div 
        className={`w-full max-w-md rounded-t-3xl animate-slide-up ${
          isDark ? 'bg-[#0D1424]' : 'bg-white'
        }`}
        style={{ maxHeight: '75vh', paddingBottom: '100px' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-white/5">
          <h2 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('weightHistory.title')}
          </h2>
          <button onClick={handleClose} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
            <X className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-gray-400'}`} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 100px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : logs.length === 0 ? (
            <p className={`text-center py-8 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('weightHistory.noRecords')}
            </p>
          ) : (
            <div className="space-y-2">
              {logs.map(log => {
                const expected = getExpected(log.date);
                const diff = expected ? (log.weight - parseFloat(expected)).toFixed(2) : null;
                const diffNum = diff ? parseFloat(diff) : 0;
                const status = !diff ? 'on' : Math.abs(diffNum) < 0.1 ? 'on' : diffNum < 0 ? 'under' : 'over';

                return (
                  <div 
                    key={log.log_id}
                    className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                          {new Date(log.date).toLocaleDateString(language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </p>
                        <p className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatNumber(convertWeight(log.weight), 1)} <span className="text-base font-normal">{weightUnit}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {diff && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            status === 'under' ? 'bg-green-500/10 text-green-500' :
                            status === 'over' ? 'bg-red-500/10 text-red-500' :
                            'bg-blue-500/10 text-blue-500'
                          }`}>
                            {diffNum > 0 ? '+' : ''}{formatNumber(convertWeight(diffNum), 1)} {weightUnit}
                          </span>
                        )}
                        <button 
                          onClick={() => startEdit(log)}
                          className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                        >
                          <Pencil className={`w-4 h-4 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
                        </button>
                        <button 
                          onClick={() => setConfirmDelete(log)}
                          className={`p-2 rounded-full ${isDark ? 'hover:bg-red-500/20' : 'hover:bg-red-50'}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                    {expected && (
                      <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                        {t('weightHistory.theoreticalLine')}: {formatNumber(convertWeight(parseFloat(expected)), 1)} {weightUnit}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {editingLog && (
          <div 
            className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            style={{ zIndex: 10000 }}
            onClick={() => setEditingLog(null)}
          >
            <div 
              className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
              onClick={e => e.stopPropagation()}
            >
              <h3 className={`font-display text-lg mb-4 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('weightHistory.editWeight')}
              </h3>
              <p className={`text-sm text-center mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {new Date(editingLog.date).toLocaleDateString(language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>

              {/* Weight Display */}
              <div className="text-center mb-3">
                <span className="font-display text-4xl font-bold text-[#1d3557]">
                  {editWeight}.{editDecimal}
                </span>
                <span className={`text-lg ${isDark ? 'text-white/50' : 'text-gray-500'}`}> {weightUnit}</span>
              </div>

              {/* Drum Picker */}
              <div className={`flex items-center justify-center rounded-xl overflow-hidden h-40 mb-6 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                {/* Whole number */}
                <div className="relative h-full w-24 overflow-hidden">
                  <div 
                    className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
                    style={{ scrollBehavior: 'smooth' }}
                    onScroll={(e) => {
                      const idx = Math.round(e.target.scrollTop / 40);
                      if (wholeRange[idx] !== undefined) setEditWeight(wholeRange[idx]);
                    }}
                    ref={(el) => {
                      if (el && !el.dataset.init) {
                        el.dataset.init = 'true';
                        const idx = wholeRange.indexOf(editWeight);
                        if (idx !== -1) el.scrollTop = idx * 40;
                      }
                    }}
                  >
                    <div className="h-[60px]"></div>
                    {wholeRange.map(w => (
                      <div 
                        key={w}
                        className={`h-10 flex items-center justify-center snap-center font-display ${
                          editWeight === w ? 'text-[#1d3557] font-bold text-2xl' : isDark ? 'text-white/30' : 'text-gray-300'
                        }`}
                      >
                        {w}
                      </div>
                    ))}
                    <div className="h-[60px]"></div>
                  </div>
                  <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-10 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
                </div>

                <span className={`font-display text-2xl ${isDark ? 'text-white/30' : 'text-gray-300'}`}>.</span>

                {/* Decimal */}
                <div className="relative h-full w-16 overflow-hidden">
                  <div 
                    className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
                    style={{ scrollBehavior: 'smooth' }}
                    onScroll={(e) => {
                      const idx = Math.round(e.target.scrollTop / 40);
                      if (decimalRange[idx] !== undefined) setEditDecimal(decimalRange[idx]);
                    }}
                    ref={(el) => {
                      if (el && !el.dataset.init) {
                        el.dataset.init = 'true';
                        el.scrollTop = editDecimal * 40;
                      }
                    }}
                  >
                    <div className="h-[60px]"></div>
                    {decimalRange.map(d => (
                      <div 
                        key={d}
                        className={`h-10 flex items-center justify-center snap-center font-display ${
                          editDecimal === d ? 'text-[#1d3557] font-bold text-2xl' : isDark ? 'text-white/30' : 'text-gray-300'
                        }`}
                      >
                        {d}
                      </div>
                    ))}
                    <div className="h-[60px]"></div>
                  </div>
                  <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-10 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setEditingLog(null)} className="btn-secondary flex-1">
                  {t('common.cancel')}
                </button>
                <button onClick={saveEdit} disabled={saving} className="btn-primary flex-1">
                  {saving ? t('weightHistory.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {confirmDelete && (
          <div 
            className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            style={{ zIndex: 10000 }}
            onClick={() => setConfirmDelete(null)}
          >
            <div 
              className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
              onClick={e => e.stopPropagation()}
            >
              <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-7 h-7 text-red-500" />
              </div>
              <h3 className={`font-display text-lg mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('weightHistory.deleteTitle')}
              </h3>
              <p className={`text-sm text-center mb-6 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('weightHistory.deleteConfirm', { weight: formatNumber(convertWeight(confirmDelete.weight), 1) + ' ' + weightUnit, date: new Date(confirmDelete.date).toLocaleDateString(language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US') })}
              </p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmDelete(null)} className="btn-secondary flex-1">
                  {t('common.cancel')}
                </button>
                <button 
                  onClick={() => handleDelete(confirmDelete.log_id)}
                  className="flex-1 py-3 px-4 rounded-full bg-red-500 text-white font-semibold"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
